import React, { useState } from 'react';
import {
  Box,
  Typography,
  Rating as MuiRating,
  TextField,
  Button,
  Avatar,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RatingService } from '../../services/RatingService';
import { useAuth } from '../../contexts/AuthContext';
import { useSnackbar } from 'notistack';

export const RatingComponent = ({ productId, sellerId, onRatingAdded }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [userRating, setUserRating] = useState(null);
  const [averageRating, setAverageRating] = useState(0);
  const [ratings, setRatings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  React.useEffect(() => {
    loadRatings();
  }, [productId]);

  const loadRatings = async () => {
    try {
      setIsLoading(true);
      const [ratingsData, userRatingData, avgRating] = await Promise.all([
        RatingService.getProductRatings(productId),
        currentUser ? RatingService.getUserRating(productId, currentUser.uid) : null,
        RatingService.getProductAverageRating(productId)
      ]);

      setRatings(ratingsData.ratings);
      setUserRating(userRatingData);
      setAverageRating(avgRating);
      if (userRatingData) {
        setRating(userRatingData.rating);
        setComment(userRatingData.comment || '');
      }
    } catch (error) {
      setError(t('rating.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      enqueueSnackbar(t('rating.loginRequired'), { variant: 'warning' });
      return;
    }

    if (currentUser.uid === sellerId) {
      enqueueSnackbar(t('rating.cannotRateOwnProduct'), { variant: 'error' });
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const ratingData = {
        productId,
        userId: currentUser.uid,
        userName: currentUser.displayName,
        userAvatar: currentUser.photoURL,
        rating,
        comment: comment.trim()
      };

      if (userRating) {
        await RatingService.updateRating(userRating.id, ratingData);
      } else {
        await RatingService.addRating(ratingData);
      }

      enqueueSnackbar(t('rating.submitSuccess'), { variant: 'success' });
      loadRatings();
      if (onRatingAdded) onRatingAdded();
    } catch (error) {
      setError(t('rating.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const canRate = currentUser && currentUser.uid !== sellerId;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} mb={3}>
          <MuiRating
            value={averageRating}
            precision={0.5}
            readOnly
            size="large"
          />
          <Typography variant="h6">
            {averageRating.toFixed(1)} {t('rating.outOf')} 5
          </Typography>
          <Typography color="text.secondary">
            ({ratings.length} {t('rating.reviews')})
          </Typography>
        </Stack>

        {canRate && (
          <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {userRating ? t('rating.updateReview') : t('rating.writeReview')}
            </Typography>
            <Box sx={{ mb: 2 }}>
              <MuiRating
                value={rating}
                onChange={(event, newValue) => {
                  setRating(newValue);
                }}
                size="large"
              />
            </Box>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('rating.commentPlaceholder')}
              sx={{ mb: 2 }}
            />
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || rating === 0}
            >
              {isSubmitting ? (
                <CircularProgress size={24} />
              ) : userRating ? (
                t('rating.update')
              ) : (
                t('rating.submit')
              )}
            </Button>
          </Box>
        )}

        {!canRate && currentUser && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('rating.cannotRateOwnProduct')}
          </Alert>
        )}

        {!currentUser && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('rating.loginRequired')}
          </Alert>
        )}

        <Divider sx={{ my: 3 }} />

        <Box>
          {ratings.map((review) => (
            <Box key={review.id} sx={{ mb: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                <Avatar src={review.userAvatar} alt={review.userName}>
                  {review.userName?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1">{review.userName}</Typography>
                  <MuiRating value={review.rating} readOnly size="small" />
                </Box>
              </Stack>
              {review.comment && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 7 }}>
                  {review.comment}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
}; 